import { Box, Stack } from "@mui/material";
import CheckBox from "components/Checkbox";
import Input from "components/Input";
import _ from "lodash";
import { TPGPLongTermGoal, TPGPShortTermGoal } from "models/tca";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";

const ContractPreparation = (props: { appointmentNumber?: number; goals: TPGPLongTermGoal[] }) => {
  const { appointmentNumber, goals } = props;
  const [goalsList, setGoalsList] = useState<TPGPLongTermGoal[]>([]);
  const formContext = useFormContext();
  const {
    setValue,
    control,
    formState: { errors },
  } = formContext;

  useEffect(() => {
    setGoalsList(goals || []);
  }, [goals]);

  //#region event handlers
  const handleGoalToggle = (v, goal: TPGPLongTermGoal) => {
    let pgpGoals = [...goalsList];
    let goalToUpdate = pgpGoals.find((f) => f.id === goal.id);
    goalToUpdate.isSelectedInContract = v;
    //make sure main goal is not selected, sub entries are unchecked
    if (!v) {
      goalToUpdate.shortTermGoals?.forEach((e) => (e.isSelectedInContract = v));
    }
    setValue("pgp", pgpGoals);
  };

  const handleShortGoalToggle = (v, goal: TPGPLongTermGoal, shortTermGoal: TPGPShortTermGoal) => {
    let pgpGoals = [...goalsList];
    let goalToUpdate = pgpGoals.find((f) => f.id === goal.id);
    let shortTermGoalToUpdate = goalToUpdate.shortTermGoals.find(
      (f) => f.whereIWantToBe === shortTermGoal.whereIWantToBe
    );
    shortTermGoalToUpdate.isSelectedInContract = v;
    //make sure main goal is selected if sub entry is checked
    if (goalToUpdate.shortTermGoals?.filter((f) => f.isSelectedInContract).length > 0) {
      goalToUpdate.isSelectedInContract = true;
    }
    setValue("pgp", pgpGoals);
  };
  //#endregion event handlers

  let nextAppointmentNbOfWeeks = 2;
  switch (appointmentNumber) {
    case 1:
    case 2:
      nextAppointmentNbOfWeeks = 4;
      break;
    case 3:
    case 4:
      nextAppointmentNbOfWeeks = 8;
      break;
    default:
      break;
  }

  return (
    <Stack direction={"row"} gap={6}>
      <Box className="greenContainer" flexGrow={1} flexBasis={0}>
        <Stack className="containerPadding">
          <Box className="intro">
            These are the Long Term Goal(s) and corresponding Short-Term Goals that you will work on
            and will appear on your contract until we meet again.
          </Box>
          <Stack>
            {goalsList?.map((m, mIndex) => (
              <Stack key={`goal${mIndex}`}>
                <CheckBox
                  checked={m.isSelectedInContract}
                  onChange={(v) => handleGoalToggle(v, m)}
                  label={m.longTermGoal}
                />
                {m.shortTermGoals?.map((mm, mmIndex) => (
                  <Box pl={4} key={`subGoal${mmIndex}`}>
                    <CheckBox
                      checked={mm.isSelectedInContract}
                      onChange={(v) => handleShortGoalToggle(v, m, mm)}
                      label={mm.whereIWantToBe}
                    />
                  </Box>
                ))}
              </Stack>
            ))}
          </Stack>
        </Stack>
      </Box>
      <Box className="greenContainer" flexGrow={1} flexBasis={0}>
        <Stack className="containerPadding">
          <Box className="intro">
            Enter the number of Workout Sessions that will be completed each week, and verify the
            number of weeks this will apply for.
          </Box>
          <Stack gap={2.5}>
            <Stack direction={"row"} alignContent="center" gap="40px">
              <Box className="fieldLabel" pt={"14px"}>
                Workout Sessions
              </Box>
              <Controller
                name="contract.workoutSessions"
                control={control}
                rules={{ required: true }}
                defaultValue={0}
                render={({ field: { onChange, value, ref } }) => (
                  <Stack spacing={1} flexGrow={1}>
                    <Input
                      type="number"
                      min={1}
                      value={value}
                      onChange={onChange}
                      hasError={!_.isEmpty(errors.contract?.workoutSessions)}
                    />
                    {errors.contract?.workoutSessions?.type === "required" && (
                      <h4 className="error">This field is required</h4>
                    )}
                  </Stack>
                )}
              />
            </Stack>
            <Stack direction={"row"} alignContent="center" gap="40px">
              <Box className="fieldLabel" pt={"14px"}>
                Number of Weeks
              </Box>
              <Controller
                name="contract.numberOfWeeks"
                control={control}
                rules={{ required: true }}
                defaultValue={nextAppointmentNbOfWeeks}
                render={({ field: { onChange, value, ref } }) => (
                  <Stack spacing={1} flexGrow={1}>
                    <Input
                      type="number"
                      min={1}
                      value={value}
                      onChange={onChange}
                      hasError={!_.isEmpty(errors.contract?.numberOfWeeks)}
                    />
                    {errors.contract?.numberOfWeeks?.type === "required" && (
                      <h4 className="error">This field is required</h4>
                    )}
                  </Stack>
                )}
              />
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Stack>
  );
};

export default ContractPreparation;
